import { useEffect, useMemo, useState } from "react";

import { type GridColDef } from "@mui/x-data-grid";
import { format, parseISO } from "date-fns";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { paginationModel, usePagination } from "api/hooks/usePagination";
import { useGetClinicPartnerPatients } from "api/services/clinic_partner/hooks";
import type { ClinicPartnerPatient } from "api/services/clinic_partner/schemas/dashboard";
import { useApiClient } from "api/useApiClient";
import { SearchIcon } from "assets";
import { useProfileContext } from "contexts/ProfileContext";
import { useSnackbarContext } from "contexts/SnackbarContext/useSnackbarContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import { AnalyticsEvents, AnalyticsService } from "utils/analytics";
import { useWindowSize } from "utils/hooks";

import { StyledSearch } from "../helpers";

import { buildCsv, downloadCsv } from "./exportPatientsCsv";
import { PatientListExportAllToolbar } from "./PatientListExportAllToolbar";

const EXPORT_BATCH_SIZE = 1000;

export const PatientList: React.FC = () => {
  const { register, setValue, getValues } = useForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useApiClient();
  const { showSnackbar } = useSnackbarContext();
  const { pagination, setPagination } = usePagination();
  const { pathname } = useLocation();
  const { profile } = useProfileContext();
  invariant(profile);

  const [searchValue, setSearchValue] = useState("");

  const { data, isFetching, isLoading } = useGetClinicPartnerPatients({
    params: {
      clinic_partner_ids: [],
      limit: pagination.limit,
      page: pagination.page + 1,
      q: searchValue || undefined,
    },
  });

  const patients = data?.items || [];
  const metadata = data?.metadata;
  const [rowCountState, setRowCountState] = useState(metadata?.total_count);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const { isSmallScreen } = useWindowSize();
  const smallScreenProps = useMemo(() => (isSmallScreen ? { minWidth: 120 } : {}), [isSmallScreen]);

  useEffect(() => {
    setRowCountState(prevRowCountState =>
      metadata?.total_count !== undefined ? metadata.total_count : prevRowCountState
    );
  }, [metadata, setRowCountState]);

  const columns = useMemo((): GridColDef[] => {
    return [
      {
        field: "id",
        headerName: t("patients.id"),
        flex: 1,
        ...smallScreenProps,
      },
      { field: "external_id", headerName: t("patients.external_id"), flex: 1, ...smallScreenProps },
      {
        field: "name",
        headerName: t("patients.name"),
        flex: 1,
        ...smallScreenProps,
      },
      {
        field: "created_at",
        headerName: t("patients.header_info.joined_date"),
        valueFormatter: (value: string) => (value ? format(parseISO(value), "PP") : ""),
        flex: 1,
        ...smallScreenProps,
      },
      {
        field: "latest_note_at",
        headerName: t("partner_patients.latest_note"),
        valueFormatter: (value: string) => (value ? format(parseISO(value), "PP") : ""),
        flex: 1,
        ...smallScreenProps,
      },
      { field: "sources", headerName: t("partner_patients.clinic"), flex: 1, ...smallScreenProps },
      {
        field: "icd_codes",
        headerName: t("partner_patients.icd_code"),
        valueFormatter: value => value?.[0],
        flex: 1,
        ...smallScreenProps,
      },
      {
        field: "adherence",
        headerName: t("partner_patients.adherence"),
        renderCell: ({ row }) => <DisplayAdherence $adherence={row.adherence}>{row.adherence}%</DisplayAdherence>,
        flex: 1,
        ...smallScreenProps,
      },
      {
        field: "kickoff_appointment_at",
        headerName: t("partner_patients.kickoff_appointment_at"),
        valueFormatter: (value: string) => (value ? format(parseISO(value), "Pp") : ""),
        flex: 1,
        ...smallScreenProps,
      },
      { field: "invited_by", headerName: t("partner_patients.invited_by"), flex: 1, ...smallScreenProps },
    ];
  }, []);

  const handleExport = async () => {
    setIsExporting(true);
    setExportProgress(0);
    try {
      const all: ClinicPartnerPatient[] = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        // eslint-disable-next-line no-await-in-loop
        const res = await client.getClinicPartnerPatients({
          params: {
            clinic_partner_ids: [],
            limit: EXPORT_BATCH_SIZE,
            page,
            q: searchValue || undefined,
          },
        });
        all.push(...res.items);
        setExportProgress(all.length);
        hasMore = res.metadata.next !== null;
        page += 1;
      }
      const csv = buildCsv(all, columns);
      downloadCsv(csv, `patients_${format(new Date(), "yyyyMMdd_HHmmss")}`);
    } catch {
      showSnackbar({ message: t("partner_patients.export_failed"), severity: "error" });
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  const renderTableSearch = () => {
    return (
      <SearchContainer>
        <StyledSearch
          data-testid="search-input-patients"
          reset={() => setValue("patientSearch", "")}
          icon={SearchIcon}
          {...register("patientSearch")}
          placeholder="Search by name"
        />
        <BaseButton
          text={t("patients.search")}
          onClick={() => {
            if (searchValue !== getValues("patientSearch")) {
              setPagination({ limit: paginationModel.pageSize, page: paginationModel.page });
            }
            setSearchValue(getValues("patientSearch"));
          }}
          style={{ margin: "0 0 0 20px" }}
        />
      </SearchContainer>
    );
  };

  return (
    <div>
      {renderTableSearch()}
      <MUIDataGrid
        columns={columns}
        rows={patients}
        onCellClick={({ row }) => {
          navigate(`/patients/${row.id}`, { state: { pathname, text: "dashboard" } });
          AnalyticsService.track(AnalyticsEvents.CLINIC_PARTNER.PATIENT_PROFILE_VIEWED);
        }}
        loading={isFetching || isLoading}
        initialState={{ pagination: { paginationModel } }}
        paginationMode="server"
        onPaginationModelChange={model => setPagination({ limit: model.pageSize, page: model.page })}
        rowCount={rowCountState ?? 0}
        rowSelection={false}
        localeText={{ noRowsLabel: t("patients.no_patients") }}
        title={t("partner_enrolled_patients_table.header")}
        slots={{ toolbar: PatientListExportAllToolbar }}
        slotProps={{
          toolbar: {
            totalCount: rowCountState ?? 0,
            isExporting,
            progress: exportProgress,
            onExport: handleExport,
          },
        }}
      />
    </div>
  );
};

const DisplayNumber = styled.div`
  margin: auto;
  color: ${props => props.theme.colors.greys.dark};
  font-weight: ${props => props.theme.fontWeight.regular};
`;

const DisplayAdherence = styled(DisplayNumber)<{ $adherence: number }>`
  color: ${props => props.$adherence < 60 && props.theme.colors.orange};
`;

const SearchContainer = styled.div`
  display: flex;
  margin-bottom: 16px;

  & > button {
    flex: 0;
  }

  ${props => props.theme.belowBreakpoint} {
    justify-content: center;
  }
`;
